/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { enhanceError, isError } from '@allurion/utils';
import {
  Conversation,
  Message,
  Participant,
  SendEmailOptions,
  SendMediaOptions,
} from '@twilio/conversations';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from 'src/analytics/analytics';
import { toastError } from 'src/components/ui/toasts';
import { Logger } from 'src/services/Logger';

import TwilioConversationsService from './TwilioConversationsService';

type Props = {
  identity?: string;
  patientIdentity: string;
  /**
   * Whether this patient can have a conversation at all — i.e. they have
   * installed the mobile app.
   *
   * Required rather than defaulted, so a caller cannot silently forget it and
   * either set off a poll for a conversation that may never exist, or leave the
   * chat permanently blank.
   *
   * `useSetupTwilioConversation` only creates a conversation for a patient who
   * has installed the app, so for one who has not there is nothing to find, and
   * `waitForConversation` polls to exhaustion and reports a failure for a state
   * that is entirely normal. The consumer's own `isPatientAvailable` guard could
   * not prevent it: that guard is an early `return` in the component body, and
   * hooks run before it.
   */
  enabled: boolean;
  friendlyName?: string;
  onMessageAdded?: Function;
  onTypingStarted?: Function;
  onTypingEnded?: Function;
};

export function useTwilioConversation({ identity, patientIdentity, enabled }: Props) {
  const intl = useIntl();
  const [loading, setLoading] = useState<boolean>(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | undefined>(undefined);
  const [typing, setTyping] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { trackTwilioFailedMessage, trackTwilioChatFailure } = useTrackEvent();
  const conversationFetchRunning = useRef(false);

  const cannotJoinConversationError = intl.formatMessage({
    defaultMessage: 'Cannot join conversation with this patient. Please reload the page.',
    id: 'twilio.failed-to-join-conversation',
  });

  const sendMessage = async (
    message: string | FormData | SendMediaOptions | null,
    attributes?: any,
    emailOptions?: SendEmailOptions
  ): Promise<number> => {
    try {
      // Optional chaining here used to resolve `undefined` when there was no
      // conversation — nothing was sent, and the caller could not tell that from
      // a delivered message. `PatientChat` renders the send box for the whole of
      // the conversation lookup, so a provider can reach this before there is
      // anything to send to. A message that went nowhere is a failed send, and
      // is reported as one.
      if (!conversation) {
        throw new Error('No conversation to send the message to');
      }

      return await conversation.sendMessage(message, attributes, emailOptions);
    } catch (error) {
      trackTwilioFailedMessage({ error });

      // Rethrown, not swallowed. Returning `undefined` here made a failed send
      // indistinguishable from a successful one: the caller went on to notify
      // the patient about a message that was never delivered, count it as sent
      // and clear the provider's draft, so a clinician was shown every sign
      // their reply had gone through. The analytics call above was the only
      // trace it left anywhere.
      throw error;
    }
  };

  const getParticipants = async () => {
    await conversation?.getParticipants();
  };

  const getMessagesCount = useCallback(async () => {
    const nums = await conversation?.getMessagesCount();

    return nums;
  }, [conversation]);

  const getAllMessages = async (m: any) => {
    let tmpMsg = m;
    const allMessages = [];

    allMessages.push(...(m?.items ?? []));
    for (; tmpMsg?.hasNextPage; ) {
      // eslint-disable-next-line no-await-in-loop
      tmpMsg = await tmpMsg?.nextPage();
      allMessages.push(...(tmpMsg?.items ?? []));
    }

    return allMessages;
  };

  const onMessageAdded = async (message: Message) => {
    setMessages((m) => [...m, message]);
    // This event triggers when either the patient or the coach adds a message.
    // `conversation.uniqueName` is the patient identity and `message.author` is
    // whoever sent the message. When the message is from the patient we advance
    // the coach's read marker so the coach isn't shown as having unread messages
    // for a conversation they're actively viewing.
    if (message.author === conversation?.uniqueName) {
      await conversation?.updateLastReadMessageIndex(message.index);
    }
  };

  const onTypingStarted = (member: Participant) => {
    setTyping(member?.identity ?? '');
  };

  const onTypingEnded = () => {
    setTyping('');
  };

  const joinConversation = useCallback(async (): Promise<void> => {
    setLoading(true);
    const nums = await getMessagesCount();

    // TODO: enable lazy-loading by using pagination
    const msgs = await conversation?.getMessages(
      nums === 0 ? undefined : nums,
      undefined,
      'forward'
    );
    const allMsgs = await getAllMessages(msgs);

    if (conversation) {
      if (conversation?.status !== 'joined') {
        await conversation?.join();
      }
      conversation?.on('messageAdded', onMessageAdded);
      conversation?.on('typingStarted', onTypingStarted);
      conversation?.on('typingEnded', onTypingEnded);
      setMessages(allMsgs);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, getMessagesCount]);

  const getParticipantByIdentity = async (identity: string) => {
    const participant = await conversation?.getParticipantByIdentity(identity);

    return participant;
  };

  const setAllMessagesRead = useCallback(async () => {
    const count = await conversation?.setAllMessagesRead();

    return count;
  }, [conversation]);

  const updateAttributes = async (attr: any) => {
    const conv = await conversation?.updateAttributes(attr);

    return conv;
  };

  const getAttributes = async () => {
    const attr = await conversation?.getAttributes();

    return attr;
  };

  const getOpponentLastReadMessageIndex = (p: Participant | undefined) => p?.lastReadMessageIndex;

  const fetchConv = useCallback(async () => {
    // `enabled` is checked in both places on purpose. The effect below is what
    // actually keeps this from being called, because it also owns the loading
    // state; repeating it here keeps `fetchConv` correct on its own terms, so a
    // later caller cannot start a lookup that has no conversation to find.
    if (!enabled || !identity || !patientIdentity) {
      return;
    }

    // Claim the guard here rather than at the call site, once the prerequisites
    // above actually hold — the same reasoning `useSetupTwilioConversation`
    // records for its own ref. Claiming it before these checks meant an early
    // return while `identity` was still missing left the ref set for good, and
    // the effect's `if (conversationFetchRunning.current) return` then blocked
    // every later attempt: the chat stayed empty for that mount even once the
    // provider arrived. `identity` can be missing at mount because
    // `useCurrentProvider` keys its SWR on `user.email`, and a falsy email
    // disables the request without suspending.
    //
    // This runs before the first `await`, so the effect cannot slip a second
    // lookup past it in the same tick.
    conversationFetchRunning.current = true;

    // A lookup is starting, so put the caller back on the loading state. This
    // matters when `enabled` has just flipped false -> true, which happens
    // whenever the patient profile is revalidated with a `uuid` where there was
    // none: the disabled branch below has already cleared `loading`, and nothing
    // else sets it until `joinConversation` runs, so without this the chat
    // renders as an empty, settled conversation for the whole of the lookup.
    setLoading(true);

    try {
      setError('');
      const conv = await TwilioConversationsService.waitForConversation(identity, patientIdentity);

      if (!conv) {
        setError('Conversation not found');

        return;
      }

      setConversation(conv);
      conversationFetchRunning.current = false;
    } catch (error) {
      Logger.captureException(enhanceError(error, { prefix: 'useTwilioConversation.fetchConv' }));
      trackTwilioChatFailure({ error });
      setError(error instanceof Error ? error.message : `${error}`);

      // The lookup is over, so `loading` must stop claiming otherwise. It is
      // cleared here rather than in a `finally` on purpose: on the *success*
      // path the conversation still has to be joined and its messages fetched,
      // and `joinConversation` is what clears it once that is done. Clearing on
      // success too would briefly present an empty, settled conversation over a
      // live send box.
      setLoading(false);

      toastError(cannotJoinConversationError);
    }
  }, [cannotJoinConversationError, enabled, identity, patientIdentity, trackTwilioChatFailure]);

  useEffect(() => {
    // Every reason no lookup will start has to settle `loading` too. It begins
    // `true` and only a lookup clears it, so returning without touching it
    // turns "there is nothing to fetch" into a permanent spinner.
    //
    // Both of these are terminal rather than transient, which is what makes
    // clearing correct here: `enabled` and `patientIdentity` are derived from
    // the same patient record, and `PatientPage` suspends until it has loaded,
    // so `enabled` true with an empty `patientIdentity` means the patient
    // exists and simply has no `userid` — not that one is still on its way. A
    // missing `identity` is the opposite case, transient while the provider
    // loads, and `fetchConv` leaves `loading` alone for it deliberately: a
    // lookup really is coming.
    if (!enabled || !patientIdentity) {
      setLoading(false);

      return;
    }

    if (conversation) {
      return;
    }

    if (conversationFetchRunning.current) {
      return;
    }

    fetchConv();
  }, [conversation, enabled, fetchConv, patientIdentity]);

  useEffect(() => {
    if (conversation) {
      joinConversation().catch((error) => {
        setError(isError(error) ? error.message : `${error}`);
        toastError(cannotJoinConversationError);
        Logger.captureException(
          enhanceError(error, { prefix: 'useTwilioConversation.joinConversation' })
        );
        trackTwilioChatFailure({ error });
      });
    }

    return () => {
      conversation?.off('messageAdded', onMessageAdded);
      conversation?.off('typingStarted', onTypingStarted);
      conversation?.off('typingEnded', onTypingEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, joinConversation]);

  return {
    messages,
    loading,
    sendMessage,
    typing,
    conversation,
    error,
    getParticipantByIdentity,
    getParticipants,
    getMessagesCount,
    updateAttributes,
    setAllMessagesRead,
    getAttributes,
    getOpponentLastReadMessageIndex,
  };
}
